import { SchemaFieldTargetDto, VisualContentDto, VisualFieldDto } from 'src/app/services/api.services';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { VisualFieldNotAllowedReason } from './CanAddVisualFieldError';
import { Visual } from './Visual';
import { VisualField } from './VisualField';
import { VisualFieldRole, VisualFieldRoleInput } from './VisualFieldRole';
import { VisualFieldTarget } from './VisualFieldTarget';
import { logger } from '../utils/logger';
import { getInsightsPbiChartType, SortedChartTypeList } from 'src/app/shared/chart/chart';
import { untracked } from '@angular/core';

const LOGGER = logger('VisualFieldCollection');

export class VisualFieldCollection {
  constructor(public readonly visual: Visual) { }

  private getRole(roleInput: VisualFieldRoleInput) {
    let role: VisualFieldRole;

    if (!(roleInput instanceof VisualFieldRole)) {
      role = this.visual.fieldRoles.get(roleInput);

      if (isNullOrUndefined(role))
        throw new Error(`Invalid role kind`, { cause: role });
    }
    else {
      role = roleInput;

      if (role.visual !== this.visual) {
        throw new Error(`Invalid role`, { cause: role });
      }
    }

    return role;
  }


  isEmpty() {
    return !this.visual.fieldRoles.value.some(r => r.fields.exludingInvalidCalc().length > 0);
  }

  size() {
    return this.visual.fieldRoles.value.reduce((n, r) => n + r.fields.value().length, 0);
  }

  /**
   * Indicates whether the field is allowed in the visual.
   * Returns null when the field is allowed.
   * Otherwise returns {@link VisualFieldNotAllowedReason} describing reason for why
   * the field should not be added to the visual. */
  fieldAllowed(
    role: VisualFieldRoleInput,
    field: VisualField | VisualFieldTarget): VisualFieldNotAllowedReason | null {
    return this.visual.fieldRoles.get(role).fieldAllowed(field);
  }

  /**
   * Indicates whether the field has been added to the visual.
   * Note - if the field is added to another visual, an error will be thrown. */
  has(field: VisualField): boolean;
  has(target: VisualFieldTarget, ignoreAggregation?: boolean): boolean;
  has(field: VisualField | VisualFieldTarget, ignoreAggregation?: boolean) {
    if (field instanceof VisualFieldTarget) {
      for (let f of this.all()) {
        if (f.target.equals(field))
          return true;

        if (ignoreAggregation && f.target.sameSource(field))
          return true;
      }

      return false;
    }
    else {
      let role = field.role();
      if (!role)
        return false;

      return this.getRole(role).fields.has(field);
    }
  }

  /**
   * Positions a field within the visual.
   * If the field is not present in the visual, it will be added.
   * If the field is present in the visual, it will be moved.
   * If index is null or undefined, the field will be moved to the end of the role. */
  place(field: VisualField, role: VisualFieldRoleInput, index?: number) {
    this.getRole(role).fields.place(field, index);
  }

  add(role: VisualFieldRoleInput, field: VisualField) {
    this.getRole(role).fields.add(field);
  }

  insert(field: VisualField, role: VisualFieldRoleInput, index: number) {
    this.getRole(role).fields.insert(field, index);
  }

  insertBefore(field: VisualField, role: VisualFieldRoleInput, position: VisualField) {
    this.getRole(role).fields.insertBefore(field, position);
  }

  insertAfter(field: VisualField, role: VisualFieldRoleInput, position: VisualField) {
    this.getRole(role).fields.insertAfter(field, position);
  }

  replace(field: VisualField, role: VisualFieldRoleInput, position: VisualField) {
    this.getRole(role).fields.replace(field, position);
  }

  remove(field: VisualField) {
    let role = field.role();
    if (!role)
      return false;

    this.getRole(role).fields.remove(field);
    return true;
  }

  clear(role: VisualFieldRoleInput) {
    this.getRole(role).fields.clear();
  }

  clearAll() {
    for (let r of this.visual.fieldRoles) 
      r.fields.clear();
  }

  *all(getFieldArgs: GetFieldArgs = {includeDisabled: false, includeInvalidCalculatedField: true}): Iterable<VisualField> {
    for (let r of this.visual.fieldRoles) {
      if (getFieldArgs?.includeDisabled || r.isEnabled()) {
        for (let f of r.fields) {
          if (getFieldArgs?.includeInvalidCalculatedField || !f.isInvalidCalculatedField())
          yield f;
        }
      }
    }
  }

  hasInvalidCalculatedField() {
    return [...this.all()].some(f => f.isInvalidCalculatedField());
  }

  find(target: SchemaFieldTargetDto, getFieldArgs?: GetFieldArgs): VisualField | null;
  find(target: VisualFieldTarget, getFieldArgs?: GetFieldArgs): VisualField | null; 
  find(target: SchemaFieldTargetDto | VisualFieldTarget, getFieldArgs?: GetFieldArgs): VisualField | null {
    target = (target instanceof VisualFieldTarget)
      ? target
      : untracked(() => this.visual.context.visualFieldTargetResolver()).find(target);

    for (let f of this.all(getFieldArgs)) {
      if (f.target.sameSource(target))
        return f;
    }

    return null;
  }

  filter(predicate: (f: VisualField) => boolean, getFieldArgs?: GetFieldArgs) {
    return [...this.all(getFieldArgs)].filter(predicate);
  }

  some(predicate: (f: VisualField) => boolean, getFieldArgs?: GetFieldArgs): boolean {
    return [...this.all(getFieldArgs)].some(predicate);
  }

  count(predicate: (f: VisualField) => boolean, getFieldArgs?: GetFieldArgs): number {
    return [...this.all(getFieldArgs)].reduce((n, f) => n + (predicate(f) ? 1 : 0), 0);
  }

  addFromContentDto(dto: VisualContentDto) {
    let pbiChartType = getInsightsPbiChartType(dto.type);
    if (!pbiChartType) {
      LOGGER.error(`Unknown PbiChartType '${dto.type}'`);
      return;
    }

    for (let f of dto.fields) {
      let role: VisualFieldRole;

      let usage = f.usage?.toLowerCase();
      if (!usage.startsWith(VisualFieldRole.Usage.Prefix)) {
        let pbiDataRole = Object.values(pbiChartType.dataRoles)
          .filter(x => !!x)
          .find(x => x.name.toLowerCase() == usage);

        if (pbiDataRole) {
          role = this.visual.fieldRoles.find(pbiDataRole.kind);
          if (!role) {
            LOGGER.error(`Could not find role for field usage '${f.usage}'`, { v: this.visual, f });
            continue;
          }
        }
      }

      if (!role) {
        role = this.visual.fieldRoles.find(usage);

        if (!role) {
          LOGGER.error(`Could not find role for field usage '${f.usage}'`, { v: this.visual, f });
          continue;
        }
      }

      let target = untracked(() => this.visual.context.visualFieldTargetResolver()).find(f.target);
      let field = new VisualField(target);

      field.customDisplayName.set(f.customName);
      field.sorted.set(!isNullOrUndefined(f.sortDirection));
      field.sortOrder.set(f.sortOrder);
      field.sortDirection.set(f.sortDirection);

      this.add(role, field);
    }
  }
}

export interface GetFieldArgs{
  includeDisabled?: boolean,
  includeInvalidCalculatedField?: boolean;
}
